import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Banknote, CheckCircle2, Clock, ExternalLink, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

// Provider payment-processing settings: accepted methods + Stripe Connect onboarding + earnings.
// Lives inside the Settings page.
export function PaymentProcessing() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [pm, setPm] = useState(null);
  const [savingPm, setSavingPm] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/connect/status");
      setStatus(data);
      const e = await api.get("/connect/earnings");
      setEarnings(e.data);
      const m = await api.get("/payment-methods");
      setPm(m.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    const flag = params.get("stripe");
    if (flag === "return") toast.success("Welcome back — refreshing your payout status…");
    if (flag === "refresh") toast.message("Let's continue setting up payouts.");
    if (flag) { params.delete("stripe"); setParams(params, { replace: true }); }
  }, [params, setParams]);

  const startOnboarding = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setConnecting(false);
    }
  };

  const enabled = status?.payouts_enabled;
  const started = status?.connected;

  const toggleMethod = (key) => {
    setPm((p) => {
      const has = p.methods.includes(key);
      if (key === "stripe" && !p.stripe_allowed && !has) { toast.error("Upgrade to a paid plan to enable Stripe card payments."); return p; }
      return { ...p, methods: has ? p.methods.filter((x) => x !== key) : [...p.methods, key] };
    });
  };
  const savePm = async () => {
    setSavingPm(true);
    try {
      await api.put("/payment-methods", { methods: pm.methods, zelle_handle: pm.zelle_handle, venmo_handle: pm.venmo_handle });
      toast.success("Payment methods saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSavingPm(false); }
  };

  return (
    <div className="mt-6" data-testid="payment-processing-section">
      <div className="flex items-center gap-2"><Banknote className="w-5 h-5 text-primary" /><h2 className="font-display text-xl font-medium">Payment processing</h2></div>
      <p className="text-sm text-muted-foreground mt-1">Choose how customers pay you, and connect Stripe to accept card payments and receive payouts for services and packages on BookdHUB.</p>

      {pm && (
        <div className="bg-white border border-border rounded-sm p-6 mt-4" data-testid="payment-methods-card">
          <h3 className="font-display text-lg font-medium">Accepted payment methods</h3>
          <p className="text-sm text-muted-foreground mt-1">Pick how customers pay the remaining balance. Enabled methods appear as badges on your services; Zelle/Venmo handles are shown to the customer at checkout.</p>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            {[["cash", "Cash"], ["zelle", "Zelle"], ["venmo", "Venmo"], ["stripe", "Stripe card"]].map(([k, label]) => {
              const on = pm.methods.includes(k);
              const locked = k === "stripe" && !pm.stripe_allowed;
              return (
                <button key={k} type="button" onClick={() => toggleMethod(k)} disabled={locked}
                  className={`text-left rounded-sm border p-3 transition-colors ${on ? "border-primary bg-primary/[0.05]" : "border-border"} ${locked ? "opacity-60 cursor-not-allowed" : ""}`}
                  data-testid={`pm-toggle-${k}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{label}</span>
                    {on ? <CheckCircle2 className="w-4 h-4 text-primary" /> : <span className="w-4 h-4 rounded-full border-2 border-muted-foreground/40" />}
                  </div>
                  {locked && <p className="text-xs text-amber-700 mt-1">Upgrade to a paid plan to enable Stripe</p>}
                  {k === "stripe" && on && <p className="text-xs text-muted-foreground mt-1">Complete Stripe setup below to receive card payments &amp; enable packages.</p>}
                </button>
              );
            })}
          </div>
          {pm.methods.includes("zelle") && (
            <div className="mt-3"><label className="text-sm font-medium">Zelle handle (shown to customers at checkout)</label>
              <input value={pm.zelle_handle || ""} onChange={(e) => setPm({ ...pm, zelle_handle: e.target.value })} placeholder="email or phone" className="mt-1 w-full rounded-sm border border-border px-3 py-2 text-sm" data-testid="zelle-handle-input" /></div>
          )}
          {pm.methods.includes("venmo") && (
            <div className="mt-3"><label className="text-sm font-medium">Venmo handle (shown to customers at checkout)</label>
              <input value={pm.venmo_handle || ""} onChange={(e) => setPm({ ...pm, venmo_handle: e.target.value })} placeholder="@username" className="mt-1 w-full rounded-sm border border-border px-3 py-2 text-sm" data-testid="venmo-handle-input" /></div>
          )}
          <Button className="rounded-sm mt-4" onClick={savePm} disabled={savingPm} data-testid="save-payment-methods-btn">{savingPm ? "Saving…" : "Save payment methods"}</Button>
        </div>
      )}

      {pm?.stripe_allowed && (loading ? (
        <div className="py-16 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : (
        <div className="bg-white border border-border rounded-sm p-6 mt-6" data-testid="payouts-card">
          {enabled ? (
            <div className="flex items-start gap-3" data-testid="payouts-enabled-state">
              <ShieldCheck className="w-6 h-6 text-green-600 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-display text-lg font-medium">Payouts enabled</h3>
                <p className="text-sm text-muted-foreground mt-1">Your Stripe account is fully set up and ready to receive transfers. You're all set.</p>
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button variant="outline" className="rounded-sm" onClick={loadStatus} data-testid="refresh-status-btn">Refresh status</Button>
                  <Button variant="outline" className="rounded-sm" onClick={startOnboarding} disabled={connecting} data-testid="update-details-btn">
                    {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Update bank details<ExternalLink className="w-4 h-4 ml-1.5" /></>}
                  </Button>
                </div>
              </div>
            </div>
          ) : started ? (
            <div className="flex items-start gap-3" data-testid="payouts-pending-state">
              <Clock className="w-6 h-6 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-display text-lg font-medium">Finish setting up payouts</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  You've started onboarding but Stripe still needs a few details before you can receive payouts
                  {status?.transfers_status ? <> (status: <b>{status.transfers_status}</b>)</> : null}.
                </p>
                <Button className="rounded-sm mt-4" onClick={startOnboarding} disabled={connecting} data-testid="continue-onboarding-btn">
                  {connecting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Redirecting…</> : <>Continue setup<ExternalLink className="w-4 h-4 ml-1.5" /></>}
                </Button>
                <Button variant="ghost" className="rounded-sm mt-4 ml-2" onClick={loadStatus} data-testid="refresh-status-btn">Refresh status</Button>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3" data-testid="payouts-notstarted-state">
              <CheckCircle2 className="w-6 h-6 text-muted-foreground shrink-0 mt-0.5" />
              <div>
                <h3 className="font-display text-lg font-medium">Set up payouts</h3>
                <p className="text-sm text-muted-foreground mt-1">Connect your account with Stripe — it takes a couple of minutes. You'll be redirected to Stripe's secure onboarding and returned here when done.</p>
                <Button className="rounded-sm mt-4" onClick={startOnboarding} disabled={connecting} data-testid="connect-stripe-btn">
                  {connecting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Redirecting…</> : <>Connect with Stripe<ExternalLink className="w-4 h-4 ml-1.5" /></>}
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}

      {pm?.stripe_allowed && (
        <>
          <p className="text-xs text-muted-foreground mt-4">Payments are securely processed by Stripe. BookdHUB never stores your bank details.</p>
          {status?.platform_fee_percent > 0 && (
            <p className="text-xs text-muted-foreground mt-1" data-testid="platform-fee-note">
              BookdHUB retains a {status.platform_fee_percent}% platform fee on each payout.
            </p>
          )}
        </>
      )}

      {earnings && (
        <div className="mt-10" data-testid="earnings-section">
          <h3 className="font-display text-xl font-medium mb-4">Earnings</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[["Gross", earnings.summary.gross], ["Platform fees", earnings.summary.platform_fees], ["Your net", earnings.summary.net], ["Paid out", earnings.summary.paid_out]].map(([lbl, val]) => (
              <div key={lbl} className="bg-white border border-border rounded-sm p-4" data-testid={`earnings-${lbl.toLowerCase().replace(/ /g, '-')}`}>
                <div className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground">{lbl}</div>
                <div className="font-display text-xl font-semibold mt-1.5">${val}</div>
              </div>
            ))}
          </div>
          {earnings.summary.pending > 0 && (
            <p className="text-sm text-amber-700 mt-3" data-testid="earnings-pending">
              ${earnings.summary.pending} is eligible for payout{status?.payouts_enabled ? " and will transfer automatically" : " — finish payout onboarding above to receive it"}.
            </p>
          )}
          {earnings.items.length > 0 && (
            <div className="bg-white border border-border rounded-sm overflow-x-auto mt-4">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr><th className="p-4">Service</th><th className="p-4">Date</th><th className="p-4">Gross</th><th className="p-4">Fee</th><th className="p-4">Net</th><th className="p-4">Payout</th></tr>
                </thead>
                <tbody>
                  {earnings.items.slice(0, 20).map((it) => (
                    <tr key={it.booking_id} className="border-t border-border" data-testid={`earning-row-${it.booking_id}`}>
                      <td className="p-4">{it.service_title}{it.settlement_gross > 0 && <span className="block text-[11px] text-muted-foreground">incl. ${it.settlement_gross} parts &amp; labor</span>}</td>
                      <td className="p-4 text-muted-foreground">{it.date}</td>
                      <td className="p-4">${it.gross}</td>
                      <td className="p-4 text-muted-foreground">-${it.platform_fee}</td>
                      <td className="p-4 font-medium text-primary">${it.net}</td>
                      <td className="p-4">
                        <span className={`text-xs px-2 py-0.5 rounded-sm font-medium ${it.payout_status === "paid" ? "bg-green-100 text-green-800" : it.payout_status === "failed" ? "bg-red-100 text-red-700" : it.payout_eligible ? "bg-amber-100 text-amber-800" : "bg-muted text-muted-foreground"}`}>
                          {it.payout_status === "paid" ? "Paid out" : it.payout_status === "pending_onboarding" ? "Awaiting onboarding" : it.payout_status === "failed" ? "Failed" : it.payout_eligible ? "Eligible" : "Held"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {earnings?.direct_payments && earnings.direct_payments.items.length > 0 && (
        <div className="mt-10" data-testid="direct-payments-section">
          <h3 className="font-display text-xl font-medium mb-1">Direct payments</h3>
          <p className="text-sm text-muted-foreground mb-4">Balances you collect directly from customers at the time of service — paid to you off-platform, not through BookdHUB. Mark "Payment received" on the booking once collected.</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white border border-border rounded-sm p-4" data-testid="direct-confirmed">
              <div className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground">Confirmed collected</div>
              <div className="font-display text-xl font-semibold mt-1.5 text-green-700">${earnings.direct_payments.confirmed}</div>
            </div>
            <div className="bg-white border border-border rounded-sm p-4" data-testid="direct-pending">
              <div className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground">Pending collection</div>
              <div className="font-display text-xl font-semibold mt-1.5 text-amber-700">${earnings.direct_payments.pending}</div>
            </div>
          </div>
          <div className="bg-white border border-border rounded-sm overflow-x-auto mt-4">
            <table className="w-full text-sm min-w-[560px]">
              <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr><th className="p-4">Service</th><th className="p-4">Date</th><th className="p-4">Total</th><th className="p-4">Your balance</th><th className="p-4">Status</th></tr>
              </thead>
              <tbody>
                {earnings.direct_payments.items.slice(0, 20).map((it) => (
                  <tr key={it.booking_id} className="border-t border-border" data-testid={`direct-row-${it.booking_id}`}>
                    <td className="p-4">{it.service_title}</td>
                    <td className="p-4 text-muted-foreground">{it.date}</td>
                    <td className="p-4">${it.total}</td>
                    <td className="p-4 font-medium text-primary">${it.balance}</td>
                    <td className="p-4"><span className={`text-xs px-2 py-0.5 rounded-sm font-medium ${it.confirmed ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>{it.confirmed ? "Confirmed" : "Awaiting"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
